import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { isInApp } from '@/libs/comeAppSDK';
import { showModalPhoneEdit } from '@/features/user-center/components/phone-edit';
import { showModalEmailOrPhoneEdit } from '@/features/user-center/components/email-or-phone-edit';
import useUserInfo from '@/features/user-center/hooks/useUserInfo';

/**
 * Hook to prompt user to bind account (phone or email) based on app context
 * 
 * @returns {Function} promptBindAccount - Function to trigger the bind account modal
 * 
 * @example
 * const { promptBindAccount } = useBindAccountPrompt();
 * 
 * // Call when needed
 * await promptBindAccount();
 */
const useBindAccountPrompt = () => {
  const t = useTranslations('personal');
  const { userInfo } = useUserInfo();

  const promptBindAccount = useCallback(async () => {
    const inApp = isInApp();
    const hasPhone = !!userInfo?.phone;
    const hasEmail = !!userInfo?.email;

    // If in app and no phone, show phone edit modal
    if (inApp && !hasPhone && !hasEmail) {
      await showModalPhoneEdit({ cancelText: t('common.skip') });
      return;
    }

    // If not in app and no phone or email, show email or phone edit modal
    if (!inApp && !hasPhone && !hasEmail) {
      await showModalEmailOrPhoneEdit({ cancelText: t('common.skip') });
      return;
    }
  }, [userInfo, t]);

  return {
    promptBindAccount,
  };
};

export default useBindAccountPrompt;

